import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

const fmtBytes = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return '—';
  if (v === 0) return '0 B';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let x = v, i = 0;
  while (x >= 1024 && i < units.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x >= 10 ? 0 : 1)} ${units[i]}`;
};

const fmtNum = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v)) return '—';
  return v.toLocaleString();
};

/**
 * "Data usage by tenant" panel. Lives in the Tenant Routing tab of the
 * ClickHouse orchestration page. One cluster at a time — pick from the
 * dropdown, see every org that has rows on that cluster ranked by
 * estimated bytes on disk, with a per-table breakdown tooltip.
 *
 * <p>Row numbers are exact (COUNT per org_id). Bytes are an estimate —
 * ClickHouse doesn't track per-row disk usage natively, so the service
 * pro-rates each table's total {@code bytes_on_disk} by that tenant's
 * fraction of rows.
 */
const ClickHouseTenantUsagePanel = ({ clusters, organizations }) => {
  const [clusterId, setClusterId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clusterId && clusters && clusters.length > 0) {
      setClusterId(String(clusters[0].id));
    }
  }, [clusters, clusterId]);

  const orgNameById = useMemo(() => {
    const m = new Map();
    (organizations || []).forEach((o) => m.set(Number(o.id), o));
    return m;
  }, [organizations]);

  const load = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await billingService.clusterTenantUsage(clusterId);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Load failed');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { load(); }, [load]);

  const totalBytes = rows.reduce((s, r) => s + (Number(r.estTotalBytes) || 0), 0);
  const totalRows = rows.reduce((s, r) => s + (Number(r.totalRows) || 0), 0);

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Data usage by tenant</Typography>
        <HelpIcon docKey="tenant_usage" />
        <Box sx={{ flex: 1 }} />
        <Select
          size="small"
          value={clusterId}
          onChange={(e) => setClusterId(e.target.value)}
          sx={{ minWidth: 200 }}
          disabled={!clusters || clusters.length === 0}
        >
          {(clusters || []).map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
          ))}
        </Select>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && rows.length === 0 && !error && (
        <Alert severity="info">
          No tenant rows found on this cluster yet. Data appears here once tenants start ingesting
          into one of the multi-tenant tables (metrics, spans, logs, rum_*, service_operations).
        </Alert>
      )}

      {rows.length > 0 && (
        <>
          <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
            <Summary label="Tenants with data" value={rows.length} />
            <Summary label="Total rows across all tenants" value={fmtNum(totalRows)} />
            <Summary label="Estimated total size" value={fmtBytes(totalBytes)} />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell align="right">Est. size</TableCell>
                <TableCell align="right">% of shard</TableCell>
                <TableCell>Per-table breakdown</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const orgId = Number(r.orgId);
                const org = orgNameById.get(orgId);
                const label = org
                  ? `${org.name || org.organizationName || org.slug || org.organizationSlug || ''}`
                  : `org ${orgId}`;
                const slug = org?.slug || org?.organizationSlug;
                const pct = totalBytes > 0
                  ? (Number(r.estTotalBytes) / totalBytes * 100).toFixed(1)
                  : '0.0';
                const breakdown = Object.entries(r.perTable || {})
                  .sort((a, b) => Number(b[1].estBytes || 0) - Number(a[1].estBytes || 0));
                return (
                  <TableRow key={orgId}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{label}</Typography>
                        {slug && <Typography variant="caption" color="text.secondary">{slug}</Typography>}
                        {!org && orgId === 0 && (
                          <Tooltip title="Rows with org_id=0 come from system/admin writes that bypass tenant context. Investigate unexpected volume.">
                            <Chip label="system/unassigned" size="small" sx={{ ml: 1 }} color="warning" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{fmtNum(r.totalRows)}</TableCell>
                    <TableCell align="right">{fmtBytes(r.estTotalBytes)}</TableCell>
                    <TableCell align="right">{pct} %</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {breakdown.map(([t, v]) => (
                          <Tooltip key={t}
                                   title={`${fmtNum(v.rows)} rows · ${fmtBytes(v.estBytes)}`}>
                            <Chip
                              size="small"
                              label={`${t}: ${fmtBytes(v.estBytes)}`}
                              variant="outlined"
                              sx={{ fontSize: 11 }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </Paper>
  );
};

const Summary = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6">{value ?? '—'}</Typography>
  </Box>
);

export default ClickHouseTenantUsagePanel;
